import { useMemo } from "react";
import type { ChamadoKanban } from "../../../types/api";
import { filtrarQuadroPorBusca } from "../gestao/busca";
import { useChamadosKanban } from "./useChamados";
import {
  useDashboardFilters,
  SLA_DEFAULT_VALUES,
  isStatusUnmarkedByDefault,
} from "./useDashboardFilters";
import { useFilterOptions } from "./useFilterOptions";
import { useProdutosSalvos } from "./useProdutosSalvos";

export const useDashboardData = () => {
  const { options: filterOptions, isLoading: isLoadingOptions } =
    useFilterOptions();

  const {
    filters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    getAPIFilters,
    mesApuracaoOptions,
  } = useDashboardFilters(filterOptions);

  const {
    data: chamados = [],
    isLoading,
    error,
    refetch,
    isFetching,
    dataUpdatedAt,
  } = useChamadosKanban(getAPIFilters);

  // O filtro de produto volta como a pessoa deixou e é salvo a cada mudança.
  // Parte do estado mais recente: se a preferência chega no mesmo ciclo em que
  // status e severidade nascem marcados, uma cópia de `filters` desfaria isso.
  useProdutosSalvos(filters.produtos, (produtos) => updateFilters((prev) => ({ ...prev, produtos })));

  // As opções são os produtos dos chamados carregados, mais os já marcados — um
  // produto salvo sem chamado no mês continua à vista para ser desmarcado.
  const produtoOptions = useMemo(() => {
    const nomes = new Set((chamados as ChamadoKanban[]).map((c) => c.projeto).filter(Boolean));
    filters.produtos.forEach((p) => nomes.add(p));
    return [...nomes].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [chamados, filters.produtos]);

  const novasFuncionalidadesOption = useMemo(() => {
    const options = filterOptions.categorias.map((c) => ({
      value: c.id.toString(),
      label: c.nome,
    }));

    return options.find((opt) => /novas funcionalidades/i.test(opt.label));
  }, [filterOptions.categorias]);

  const chamadosFiltrados = useMemo(() => {
    let lista = filtrarQuadroPorBusca(chamados as ChamadoKanban[], filters.texto);
    if (filters.produtos.length > 0)
      lista = lista.filter((chamado) => filters.produtos.includes(chamado.projeto));
    if (!novasFuncionalidadesOption) return lista;

    if (filters.categoriaGrupo === "novas_funcionalidades") {
      lista = lista.filter(
        (chamado) => chamado.categoria === novasFuncionalidadesOption.label,
      );
    } else {
      lista = lista.filter(
        (chamado) => chamado.categoria !== novasFuncionalidadesOption.label,
      );
    }

    // Filtro client-side por SLA status — pula quando todos os defaults estão marcados (sem restrição)
    const slaIsDefault =
      filters.slaStatus.length === SLA_DEFAULT_VALUES.length &&
      SLA_DEFAULT_VALUES.every((v) => filters.slaStatus.includes(v));
    if (filters.slaStatus.length > 0 && !slaIsDefault) {
      lista = lista.filter((chamado) => {
        const sla = chamado.slaInfo?.statusSla?.toLowerCase() ?? "";
        return filters.slaStatus.some((filtro) => {
          switch (filtro) {
            case "pausado":
              return sla.includes("pausado");
            case "aguardando_fila":
              return sla.includes("aguardando na fila");
            case "concluido_prazo":
              return sla === "concluído no prazo";
            case "concluido_atraso":
              return sla === "concluído com atraso";
            default:
              return false;
          }
        });
      });
    }

    return lista;
  }, [chamados, filters.texto, filters.categoriaGrupo, filters.slaStatus, filters.produtos, novasFuncionalidadesOption]);

  // Calcular contadores — filtro "ativo" = diferente do default (todos marcados, exceto Cancelado)
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    const totalSev = filterOptions.severidades.length;
    const statusDefaults = filterOptions.status
      .filter((s) => !isStatusUnmarkedByDefault(s.nome))
      .map((s) => s.id.toString());
    const statusIsDefault =
      filters.status.length === statusDefaults.length &&
      statusDefaults.every((id) => filters.status.includes(id));

    if (filters.texto) count++;
    if (totalSev > 0 && filters.severidade.length !== totalSev) count++;
    if (statusDefaults.length > 0 && !statusIsDefault) count++;
    if (filters.slaStatus.length !== SLA_DEFAULT_VALUES.length) count++;
    if (filters.categoriaGrupo !== "suporte") count++;
    if (filters.produtos.length > 0) count++;
    if (
      filters.mesApuracao !==
      (() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        return `${year}-${month}`;
      })()
    )
      count++;
    return count;
  }, [filters, filterOptions]);

  const filteredCount = chamadosFiltrados.length;

  return {
    // Dados
    chamados: chamadosFiltrados,
    isLoading,
    error,
    refetch,
    isFetching,
    /** Quando o servidor respondeu, em epoch. Zero enquanto nunca respondeu. */
    dataUpdatedAt,

    // Filtros
    filters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    getAPIFilters,

    // Opções de filtros
    filterOptions: {
      produto: produtoOptions,
      severidade: filterOptions.severidades.map((s) => ({
        value: s.id.toString(),
        label: s.nome,
      })),
      status: filterOptions.status.map((s) => ({
        value: s.id.toString(),
        label: s.nome,
      })),
      mesApuracao: mesApuracaoOptions,
    },
    isLoadingOptions,

    // Contadores
    activeFiltersCount,
    filteredCount,
  };
};
